import MuiCard from '@mui/material/Card'
import { styled } from '@mui/material/styles'

// ─── Root ─────────────────────────────────────────────────────────────────────
// Pure presentational surface. The interactive look (shadow, brand-subtle
// border, hover/active/focus states) is applied by `CardButton` from the
// outside — Card itself has no knowledge of interactivity.

export type CardBackground = 'transparent' | 'subtle' | 'surface'
export type CardElevation = 'none' | 'sm'
export type CardBorderColor = 'subtle' | 'brand-subtle' | 'brand-default'

export const StyledRoot = styled(MuiCard, {
  shouldForwardProp: (prop: string) => !prop.startsWith('$'),
})<{
  $noPadding?: boolean
  $background?: CardBackground
  $elevation?: CardElevation
  $borderColor?: CardBorderColor
}>(
  ({
    theme,
    $noPadding,
    $background = 'transparent',
    $elevation = 'none',
    $borderColor = 'subtle',
  }) => ({
    '&.MuiCard-root': {
      position: 'relative',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: $noPadding ? 0 : theme.spacing(2),
      backgroundColor:
        $background === 'transparent'
          ? 'transparent'
          : theme.palette.background[$background],
      boxShadow: $elevation === 'none' ? 'none' : theme.elevation[$elevation],
      border: `${theme.border.sm} solid ${theme.palette.border[$borderColor]}`,
    },
  }),
)

// ─── Layout helpers ───────────────────────────────────────────────────────────

export const Content = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
})

export const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  width: '100%',
}))

export const TitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

export const InnerColumn = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
})

export const SkeletonStack = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}))
